import type { CheckpointRevertResult } from '@crowdex/workspace-protocol';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import type { TerminalHandlers, WorkspaceClient } from '@/lib/workspaces/workspace-client';
import { WorkspaceRpcClient } from '@/lib/workspaces/workspace-rpc-client';

export function createCloudWorkspaceClient(
  runtime: CloudRuntime,
  refreshRuntime?: () => CloudRuntime,
): WorkspaceClient {
  const REQUEST_TIMEOUT_MS = 300_000;
  const terminals = new Map<string, TerminalHandlers>();
  const rpc = new WorkspaceRpcClient<CloudRuntime>({
    connection: () => {
      const current = refreshRuntime?.() ?? runtime;
      return { url: current.url, token: current.token, context: current };
    },
    requestTimeoutMs: REQUEST_TIMEOUT_MS,
    // A sandbox that starts answers its first connection late; stay under the 50 s a load
    // balancer lets a silent connection live.
    handshakeTimeoutMs: 45_000,
    connectAttempts: 3,
    retryBaseMs: 250,
    heartbeat: {
      intervalMs: 5_000,
      timeoutMs: 15_000,
      timeoutMessage: 'Workspace connection stopped responding.',
    },
    messages: {
      closed: 'Workspace connection closed.',
      connectionFailed: 'Workspace connection failed.',
      handshakeTimeout: 'Workspace connection handshake timed out.',
      invalidFrame: 'Workspace server returned an invalid frame.',
      requestTimeout: 'Workspace request timed out.',
    },
    onPush(frame) {
      if (frame.type === 'agent_output') return;
      if (frame.type === 'workspace_git_changed') return;
      if (frame.type === 'terminal_output') {
        terminals.get(frame.terminal_id)?.onOutput(frame.data);
      } else {
        const handlers = terminals.get(frame.terminal_id);
        terminals.delete(frame.terminal_id);
        handlers?.onExit(frame.code);
      }
    },
    onDisconnect() {
      for (const handlers of terminals.values()) handlers.onExit(null);
      terminals.clear();
    },
  });

  const request = (frame: Record<string, unknown>) => rpc.request(frame);
  const send = (frame: Record<string, unknown>) => rpc.send(frame);
  const connectedRuntime = () => rpc.context ?? runtime;
  return {
    ready: rpc.ready,
    async listPorts() {
      const frame = await request({ type: 'workspace_list_ports' });
      if (frame.type !== 'workspace_ports') throw new Error('Invalid ports response.');
      return frame.ports;
    },
    async listDirectory(relativePath) {
      const frame = await request({
        type: 'workspace_list_directory',
        workspace_root: connectedRuntime().workspaceRoot,
        relative_path: relativePath,
      });
      if (frame.type !== 'workspace_directory') throw new Error('Invalid directory response.');
      return frame.entries;
    },
    async readTextFile(path) {
      const frame = await request({
        type: 'workspace_read_text_file',
        workspace_root: connectedRuntime().workspaceRoot,
        path: `${connectedRuntime().workspaceRoot}/${path}`,
        line: null,
        limit: null,
      });
      if (frame.type !== 'workspace_text_file') throw new Error('Invalid file response.');
      return frame.content;
    },
    async readBinaryFile(path) {
      const frame = await request({
        type: 'workspace_read_binary_file',
        workspace_root: connectedRuntime().workspaceRoot,
        path: `${connectedRuntime().workspaceRoot}/${path}`,
      });
      if (frame.type !== 'workspace_binary_file') throw new Error('Invalid file response.');
      return frame.content;
    },
    async writeTextFile(path, content) {
      const frame = await request({
        type: 'workspace_write_text_file',
        workspace_root: connectedRuntime().workspaceRoot,
        path: `${connectedRuntime().workspaceRoot}/${path}`,
        content,
      });
      if (frame.type !== 'workspace_file_written') throw new Error('Invalid file response.');
    },
    async writeBinaryFile(path, content) {
      const frame = await request({
        type: 'workspace_write_binary_file',
        workspace_root: connectedRuntime().workspaceRoot,
        path: `${connectedRuntime().workspaceRoot}/${path}`,
        content,
      });
      if (frame.type !== 'workspace_file_written') throw new Error('Invalid file response.');
    },
    async copyFile(source, target) {
      const root = connectedRuntime().workspaceRoot;
      const frame = await request({
        type: 'workspace_copy_file',
        workspace_root: root,
        source: `${root}/${source}`,
        target: `${root}/${target}`,
      });
      if (frame.type !== 'workspace_file_written') throw new Error('Invalid file response.');
    },
    async removeFile(path) {
      const frame = await request({
        type: 'workspace_remove_file',
        workspace_root: connectedRuntime().workspaceRoot,
        path: `${connectedRuntime().workspaceRoot}/${path}`,
      });
      if (frame.type !== 'workspace_file_removed') throw new Error('Invalid file response.');
    },
    async gitReview() {
      const frame = await request({
        type: 'workspace_git_review',
        workspace_root: connectedRuntime().workspaceRoot,
      });
      if (frame.type !== 'workspace_git_review') throw new Error('Invalid Git response.');
      return frame.review;
    },
    async checkpointDiff(before, after, path, exclude) {
      const frame = await request({
        type: 'workspace_checkpoint_diff',
        workspace_root: connectedRuntime().workspaceRoot,
        before,
        after,
        path: path ?? null,
        ...(exclude === undefined || exclude.length === 0 ? {} : { exclude }),
      });
      if (frame.type !== 'workspace_checkpoint_diff' || typeof frame['patch'] !== 'string') {
        throw new Error('Invalid checkpoint response.');
      }
      return frame['patch'];
    },
    async revertCheckpoints(turns, options = {}) {
      const frame = await request({
        type: 'workspace_checkpoint_revert',
        workspace_root: connectedRuntime().workspaceRoot,
        turns,
        overwrite: options.overwrite ?? [],
        keep: options.keep ?? [],
        dry_run: options.dryRun === true,
      });
      if (frame.type !== 'workspace_checkpoint_revert')
        throw new Error('Invalid checkpoint response.');
      const result = (frame as { result?: CheckpointRevertResult }).result;
      if (typeof result?.applied !== 'boolean') throw new Error('Invalid checkpoint response.');
      return result;
    },
    async updateGitFile(path, action) {
      const frame = await request({
        type: `workspace_git_${action}`,
        workspace_root: connectedRuntime().workspaceRoot,
        path,
      });
      if (frame.type !== 'workspace_git_action_completed') {
        throw new Error('Invalid Git action response.');
      }
    },
    async spawnTerminal(terminalId, cols, rows, handlers) {
      terminals.set(terminalId, handlers);
      try {
        const frame = await request({
          type: 'terminal_spawn',
          terminal_id: terminalId,
          workspace_root: connectedRuntime().workspaceRoot,
          cols,
          rows,
        });
        if (frame.type !== 'terminal_spawned') throw new Error('Invalid terminal response.');
      } catch (error) {
        terminals.delete(terminalId);
        throw error;
      }
    },
    writeTerminal: (terminalId, data) =>
      send({ type: 'terminal_write', terminal_id: terminalId, data }),
    resizeTerminal: (terminalId, cols, rows) =>
      send({ type: 'terminal_resize', terminal_id: terminalId, cols, rows }),
    killTerminal(terminalId) {
      terminals.delete(terminalId);
      send({ type: 'terminal_kill', terminal_id: terminalId });
    },
    close() {
      for (const terminalId of terminals.keys()) {
        send({ type: 'terminal_kill', terminal_id: terminalId });
      }
      terminals.clear();
      rpc.close(new Error('Cloud workspace connection closed.'));
    },
  };
}
